<template>
  <div class="tag-info__wrapper">
    <div class="tag-info__top-section">
      <h1 class="tag-info__title">{{ Name }}</h1>
      <a-action-icon
        v-bind="actionIcon.props"
        :action-icon="actionIcon.icon"
        class="tag-info__follow-icon"
        v-on="$listeners"
      />

      <a-lazy-hydrate when-visible>
        <a-quicksearch :type="QUICKSEARCH_TYPE.TAGS" class="tag-info__search" />
      </a-lazy-hydrate>
    </div>

    <div class="tag-info__bottom-section">
      <div v-if="isCompanyTag">
        <div class="tag-info__company">
          <div class="tag-info__company-logo-container">
            <a-lazy-hydrate when-visible>
              <a-image
                :src="CompanyInfo.LogoUrl"
                alt="company-logo"
                :aspect-ratio="aspectRatios.fmDirectoryCompanyLogo"
                class="tag-info__company-logo"
              />
            </a-lazy-hydrate>
          </div>

          <div class="tag-info__company-info-container">
            <h2 class="tag-info__company-name subtitle-2">
              {{ CompanyInfo.Name }}
            </h2>
            <a-show-more
              class="tag-info__description-wrapper"
              v-bind="descriptionProps"
            >
              <a-render-html
                :html="processHtml(description)"
                class="tag-info__description text-body"
              />
            </a-show-more>
          </div>
        </div>
      </div>

      <div v-else-if="isTermTag">
        <a-show-more
          class="tag-info__description-wrapper"
          v-bind="descriptionProps"
        >
          <a-render-html
            :html="processHtml(description)"
            class="tag-info__description text-body"
          />
        </a-show-more>
      </div>
    </div>

    <div class="section-delimiter" />
  </div>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { aspectRatios } from 'enums/aspectRatios'
import AActionIcon, { ACTION_ICON } from 'shared/AActionIcon'
import AShowMore from 'shared/AShowMore'
import mixins from '@/utils/mixins'
import AQuicksearch, { QUICKSEARCH_TYPE } from 'shared/AQuickSearch'
import { processHtml } from '@/utils/helpers/processHtml'
import ARenderHtml from 'shared/ARenderHtml'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'

export const TAG_TYPE = {
  REGULAR: 'Regular',
  TERM: 'Term',
  COMPANY: 'Company'
}

export default {
  name: 'TagInfo',
  mixins: [mixins.urlGenerators, hydrationHelpers],
  components: { ARenderHtml, AActionIcon, AShowMore, AQuicksearch },
  props: {
    Id: propValidator([PROP_TYPES.STRING]),
    Name: propValidator([PROP_TYPES.STRING]),
    IsFollowed: propValidator([PROP_TYPES.BOOLEAN]),
    CompanyInfo: propValidator([PROP_TYPES.OBJECT]),
    TermInfo: propValidator([PROP_TYPES.OBJECT])
  },
  data() {
    return {
      TAG_TYPE,
      QUICKSEARCH_TYPE,
      aspectRatios,
      searchPhrase: ''
    }
  },
  computed: {
    actionIcon() {
      return {
        icon: ACTION_ICON.FOLLOW,
        props: {
          tag: {
            Id: this.Id,
            Name: this.Name,
            IsFollowed: this.IsFollowed
          }
        }
      }
    },
    isCompanyTag() {
      return !!this.CompanyInfo.Id
    },
    isTermTag() {
      return !!this.TermInfo.Id
    },
    description() {
      return this.isCompanyTag
        ? this.CompanyInfo.Description
        : this.TermInfo.Description
    },
    link() {
      return this.isCompanyTag
        ? this.generateCompanyUrl(this.CompanyInfo)
        : this.generateTermRoute(this.TermInfo)
    },
    descriptionProps() {
      return {
        rowHeight: this.$_hydrationHelpers_mobileOrDesktopValue(22, 24),
        rowCount: 4,
        to: this.link
      }
    }
  },
  methods: {
    processHtml
  }
}
</script>

<style lang="scss" scoped>
.tag-info__wrapper {
  margin-top: 30px;

  @include mobile {
    margin-top: 0;
  }

  .tag-info__top-section {
    display: flex;
    justify-content: space-between;

    @include mobile {
      flex-wrap: wrap;
    }
  }

  .tag-info__title {
    margin-right: 15px;

    @include mobile {
      order: 2;
      max-width: calc(100% - 54px);
    }
  }

  .tag-info__follow-icon {
    margin-left: auto;

    @include mobile {
      order: 3;
      margin: 0;
    }
  }

  .tag-info__search {
    width: 320px;
    margin-left: 65px;
    flex-shrink: 0;

    @include mobile {
      width: 100%;
      margin: 0 0 10px 0;
      order: 1;
    }
  }

  .tag-info__bottom-section {
    margin-top: 20px;
  }

  .tag-info__description-wrapper {
    display: block;
    max-width: 1000px;
  }

  .tag-info__description {
    /deep/ a {
      color: $c--main;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .tag-info__company {
    display: flex;

    @include mobile {
      flex-direction: column;
    }
  }

  .tag-info__company-logo-container {
    width: 162px;
    padding-right: 22px;

    @include mobile {
      margin: 0 auto 20px auto;
      padding: 0;
    }
  }

  .tag-info__company-info-container {
    max-width: 1000px;
    flex-grow: 1;
  }

  .tag-info__company-name {
    margin-bottom: 10px;
  }
}
</style>
